/**
 * WebSocket configuration
 * Base URLs are defined in environment variables, paths are defined here
 */

// Base URLs from environment
const ORDER_BOOK_BASE_URL = process.env.NEXT_PUBLIC_ORDER_BOOK_SERVICE_WEBSOCKET_URL || '';
const WALLET_BASE_URL = process.env.NEXT_PUBLIC_WALLET_MUTATOR_VIEW_WEBSOCKET_URL || '';
const CHANCE_HISTORY_BASE_URL = process.env.NEXT_PUBLIC_CHANCE_HISTORY_SERVICE_WEBSOCKET_URL || '';
const ORDER_SERVICE_BASE_URL = process.env.NEXT_PUBLIC_ORDER_SERVICE_WEBSOCKET_URL || '';
const ORDER_HISTORY_BASE_URL = process.env.NEXT_PUBLIC_HISTORY_SERVICE_WEBSOCKET_URL || '';
const SWAP_SERVICE_BASE_URL = process.env.NEXT_PUBLIC_SWAP_SERVICE_WEBSOCKET_URL || '';
// Falls back to the REST URL with `http(s)` swapped to `ws(s)` and the `/api`
// suffix stripped, so a single env var (NEXT_PUBLIC_API_NOTIFICATION_URL) is
// enough to wire both REST and WS for notifications.
const NOTIFICATION_BASE_URL =
  process.env.NEXT_PUBLIC_NOTIFICATION_WEBSOCKET_URL ||
  (process.env.NEXT_PUBLIC_API_NOTIFICATION_URL
    ? process.env.NEXT_PUBLIC_API_NOTIFICATION_URL.replace(/^http/i, 'ws').replace(/\/api\/?$/, '')
    : '');
// The futures market-data REST URL with `http(s)` swapped to `ws(s)`, as the trade zone does.
const FUTURES_MARKETDATA_BASE_URL = (
  process.env.NEXT_PUBLIC_FUTURES_MARKETDATA_SERVICE_URL || ''
).replace(/^http/i, 'ws');

/**
 * Build a WebSocket URL from a base and a path. Returns an empty string when
 * the base is empty — that way `useWebSocket` (`if (!url) return;`) skips the
 * connection entirely instead of resolving the relative path against the
 * current origin (which produces a phantom `ws://localhost:3000/...` request
 * that always fails in dev).
 */
function joinWs(base: string, path: string): string {
  return base ? `${base}${path}` : '';
}

/**
 * WebSocket endpoints configuration
 * Combines base URLs from env with specific paths
 */
export const WS_ENDPOINTS = {
  // Order Book Service endpoints (order-book.outpoll.io)
  orderBook: joinWs(ORDER_BOOK_BASE_URL, '/orderbook/ws'),
  orderBookBestBidAsk: joinWs(ORDER_BOOK_BASE_URL, '/event/ws'),

  // Chance History Service (chance-history-service.outpoll.io/ws)
  chanceHistory: CHANCE_HISTORY_BASE_URL,

  // Order Service (order-service.outpoll.io/order/ws)
  orderPlacement: ORDER_SERVICE_BASE_URL,

  // Order History Service (history-service.outpoll.io/order/ws)
  orderHistory: joinWs(ORDER_HISTORY_BASE_URL, '/order/ws'),

  // Activity History Service (history-service.outpoll.io/history/ws)
  activityHistory: joinWs(ORDER_HISTORY_BASE_URL, '/history/ws'),

  // Wallet Service endpoints (wallet-mutator-view.outpoll.io)
  balance: joinWs(WALLET_BASE_URL, '/balance/ws'),
  transactions: joinWs(WALLET_BASE_URL, '/transaction/ws'),

  // Last Trades (history-service.outpoll.io/last-trades/ws)
  lastTrades: joinWs(ORDER_HISTORY_BASE_URL, '/last-trades/ws'),

  // Swap Service (swap-service.outpoll.io/rates/ws)
  swapRates: joinWs(SWAP_SERVICE_BASE_URL, '/rates/ws'),

  // Notification Service (notification-service.outpoll.io/notifications/ws)
  notifications: joinWs(NOTIFICATION_BASE_URL, '/notifications/ws'),

  // Futures 24h tickers (futures-marketdata-service/ws/ticker/24h)
  futuresTicker: joinWs(FUTURES_MARKETDATA_BASE_URL, '/ws/ticker/24h'),

  // Futures top of book (futures-marketdata-service/ws/book/best)
  futuresBookBest: joinWs(FUTURES_MARKETDATA_BASE_URL, '/ws/book/best'),
} as const;
